import { AvField, AvForm } from "availity-reactstrap-validation";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { Bounce, ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Button, Card, CardBody, Col, Label, Row } from "reactstrap";
import api from "../../services/api";
import { setBreadcrumbItems } from "../../store/actions";


const AddStudentRegistration = (props) => {
  const navigate = useNavigate();
  document.title = "Technical Hub | Student Registration";

  const breadcrumbItems = [
    { title: "Technical Hub", link: "#" },
    { title: "Student Registrations", link: "#" },
    { title: "Add Student Registrations", link: "#" },
  ];

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [courses, setCourses] = useState([]);
  const [programNames, setProgramNames] = useState([]);
  const [technologies, setTechnologies] = useState([]);

  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedTechnology, setSelectedTechnology] = useState(null);

  const [addedBy, setAddedBy] = useState("admin");

  useEffect(() => {
    const curr_user = JSON.parse(localStorage.getItem("authUser"));
    if (curr_user) setAddedBy(curr_user.first_name);

    fetchCourses();
    props.setBreadcrumbItems("Add Student Registration", breadcrumbItems);
  }, []);

  /** Fetch all available courses */
  const fetchCourses = async () => {
    try {
      const { data } = await axios.get(`${backendUrl}get-courses`);
      setCourses(data || []);
    } catch (error) {
      console.error("Error fetching courses:", error);
      toast.error("Failed to load courses.");
    }
  };

  /** Fetch programs when course changes */
  useEffect(() => {
    if (!selectedCourse) {
      setProgramNames([]);
      setTechnologies([]);
      setSelectedProgram(null);
      setSelectedTechnology(null);
      return;
    }

    const fetchPrograms = async () => {
      try {
        const response = await axios.get(
          `${backendUrl}get-programs-by-course-id/${selectedCourse.value}`
        );
        setProgramNames(response.data.coursePrograms || []);
      } catch (error) {
        console.error("Error fetching programs:", error);
        toast.error("Failed to load programs.");
      }
    };

    fetchPrograms();
  }, [selectedCourse]);

  /** Fetch technologies when program changes */
  useEffect(() => {
    if (!selectedProgram) {
      setTechnologies([]);
      setSelectedTechnology(null);
      return;
    }

    const fetchTechnologies = async () => {
      try {
        const response = await axios.get(
          `${backendUrl}get-technology-by-program/${selectedProgram.value}`
        );

        setTechnologies(response.data.Technology || []);
      } catch (error) {
        console.error("Error fetching technologies:", error);
        toast.error("Failed to load technologies.");
      }
    };

    fetchTechnologies();
  }, [selectedProgram]);


  /** Submit Handler */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const rollNo = e.target.rollNo.value.toUpperCase().trim();

    if (!rollNo || !selectedCourse || !selectedProgram || !selectedTechnology) {
      toast.error("Please fill all required fields.");
      return;
    }

    try {
      // Validate Roll No
      const { data: user } = await axios.get(
        `${backendUrl}get-user-by-rollno/${rollNo}`
      );

      if (!user?._id) {
        toast.error("Student not found for given Roll No.");
        return;
      }

      const registrationData = {
        student_id: user._id,
        course: selectedCourse.value,
        technology: selectedTechnology.value,
        reg_id: selectedTechnology.reg_id,
        added_by: addedBy,
      };

      console.log("Registration Data:", registrationData);
      const res = await api.post(
        `${backendUrl}create-student-registration`,
        registrationData
      );

      if (res.status === 201) {
        toast.success("Student registered successfully.");

        setSelectedCourse(null);
        setSelectedProgram(null);
        setSelectedTechnology(null);

        e.target.reset();
        navigate("/student-registrations-list");
      }
    } catch (error) {
      console.error("Error:", error);
      const msg = error.response?.data?.message || "Registration failed.";
      toast.error(msg);
    }
  };

  return (
    <React.Fragment>
      <Row>
        <ToastContainer
          position="top-right"
          autoClose={4000}
          hideProgressBar={false}
          transition={Bounce}
          theme="light"
        />

        <Col xl="12">
          <Card>
            <CardBody>
              <h4 className="card-title mb-3">Add Student Registration</h4>

              <AvForm onValidSubmit={handleSubmit}>
                <Row>
                  {/* Roll No */}
                  <Col md="6">
                    <div className="mb-4">
                      <Label>Roll No <span className="text-danger">*</span></Label>
                      <AvField
                        name="rollNo"
                        placeholder="Enter Roll No"
                        required
                      />
                    </div>
                  </Col>

                  {/* Course */}
                  <Col md="6">
                    <div className="mb-4">
                      <Label>Course <span className="text-danger">*</span></Label>
                      <Select
                        value={selectedCourse}
                        onChange={(course) => {
                          setSelectedCourse(course);
                          setSelectedProgram(null);     // reset program
                          setSelectedTechnology(null);  // reset technology
                          setProgramNames([]);          // clear list
                          setTechnologies([]);          // clear list
                        }}
                        options={courses.map((c) => ({
                          value: c._id,
                          label: c.course_name,
                        }))}
                        placeholder="Choose Course"
                      />

                    </div>
                  </Col>

                  {/* Program */}
                  <Col md="6">
                    <div className="mb-4">
                      <Label>Program <span className="text-danger">*</span></Label>
                      <Select
                        value={selectedProgram}
                        onChange={(program) => {
                          setSelectedProgram(program);
                          setSelectedTechnology(null);   // reset technology
                          setTechnologies([]);           // clear list until fetched
                        }}
                        options={programNames.map((p) => ({
                          value: p.program_name,
                          label: p.program_name,
                        }))}
                        isDisabled={!selectedCourse}
                        placeholder="Choose Program"
                      />

                    </div>
                  </Col>

                  {/* Technology */}
                  <Col md="6">
                    <div className="mb-4">
                      <Label>Technology <span className="text-danger">*</span></Label>
                      <Select
                        value={selectedTechnology}
                        onChange={setSelectedTechnology}
                        options={technologies.map((t) => ({
                          value: t.technology_id,
                          label: t.technology_name,
                          reg_id: t._id,
                        }))}
                        isDisabled={!selectedProgram}
                      />


                    </div>
                  </Col>
                </Row>

                <Button color="primary" type="submit">
                  Submit
                </Button>
              </AvForm>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default connect(null, { setBreadcrumbItems })(AddStudentRegistration);
